// context/ImprovedOwnerContext.js
import React, { createContext, useState, useEffect, useCallback } from 'react';
import axios from '../axiosConfig';
import i18n from '../i18n';

export const OwnerContext = createContext();

export const OwnerProvider = ({ children }) => {
    const [owner, setOwner] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const validateToken = () => {
        const token = localStorage.getItem('jwt');
        if (!token || typeof token !== 'string' || token.length < 10) {
            throw new Error('Invalid or missing token');
        }
        return token;
    };

    const fetchOwnerData = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            const token = validateToken();
            const response = await axios.get('/api/v1/owner/me', {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                timeout: 10000
            });

            if (response.data) {
                setOwner(response.data);
            } else {
                throw new Error('No owner data received');
            }
        } catch (err) {
            console.error('Failed to fetch owner details:', err);

            if (err.response?.status === 401) {
                setError(i18n.t('common:sessionExpired'));
                localStorage.removeItem('jwt');
                localStorage.removeItem('refreshToken');
            } else if (err.response?.status === 403) {
                setError(i18n.t('common:noPermission'));
            } else if (err.code === 'ECONNABORTED') {
                setError(i18n.t('common:timeout'));
            } else {
                setError(i18n.t('common:fetchOwnerFailed'));
            }
        } finally {
            setLoading(false);
        }
    }, []);

    const refreshOwnerData = useCallback(async () => {
        if (loading) return;
        await fetchOwnerData();
    }, [fetchOwnerData, loading]);

    useEffect(() => {
        fetchOwnerData();
    }, [fetchOwnerData]);

    const value = {
        owner,
        loading,
        error,
        refreshOwnerData
    };

    return (
        <OwnerContext.Provider value={value}>
            {children}
        </OwnerContext.Provider>
    );
};